import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as workInProgressAPI from '../../api/AccountsAPI/workInProgressAPI';

// Async Thunks
// =============================================

export const fetchCCForWorkInProgress = createAsyncThunk(
    'workInProgress/fetchCCForWorkInProgress',
    async (params, { rejectWithValue }) => {
        try {
            const response = await workInProgressAPI.getCCForWorkInProgress(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers');
        }
    }
);

export const fetchClientsByCCForWorkProgress = createAsyncThunk(
    'workInProgress/fetchClientsByCCForWorkProgress',
    async (ccCode, { rejectWithValue }) => {
        try {
            const response = await workInProgressAPI.getClientsByCCForWorkProgress(ccCode);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Clients');
        }
    }
);

export const fetchSubClientsByClientForWP = createAsyncThunk(
    'workInProgress/fetchSubClientsByClientForWP',
    async (params, { rejectWithValue }) => {
        try {
            const response = await workInProgressAPI.getSubClientsByClientForWP(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch Sub-Clients');
        }
    }
);

export const fetchClientPOforWP = createAsyncThunk(
    'workInProgress/fetchClientPOforWP',
    async (params, { rejectWithValue }) => {
        try {
            const response = await workInProgressAPI.getClientPOforWP(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to fetch PO Numbers');
        }
    }
);

export const saveWorkProgress = createAsyncThunk(
    'workInProgress/saveWorkProgress',
    async (params, { rejectWithValue }) => {
        try {
            const response = await workInProgressAPI.saveWorkProgress(params);
            return response;
        } catch (error) {
            return rejectWithValue(error.Message || error.message || 'Failed to save Work Progress');
        }
    }
);

// Initial State
// =============
const initialState = {
    costCenters: [],
    clients: [],
    subClients: [],
    poNumbers: [],
    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        costCenters: false,
        clients: false,
        subClients: false,
        poNumbers: false,
        save: false,
    },

    errors: {
        costCenters: null,
        clients: null,
        subClients: null,
        poNumbers: null,
        save: null,
    },
};

const workInProgressSlice = createSlice({
    name: 'workInProgress',
    initialState,
    reducers: {
        clearClients: (state) => {
            state.clients = [];
            state.errors.clients = null;
        },
        clearSubClients: (state) => {
            state.subClients = [];
            state.errors.subClients = null;
        },
        clearPONumbers: (state) => {
            state.poNumbers = [];
            state.errors.poNumbers = null;
        },
        clearSaveResult: (state) => {
            state.saveResult = null;
            state.saveStatus = null;
            state.errors.save = null;
        },
        resetWorkInProgress: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCForWorkInProgress.pending, (state) => {
                state.loading.costCenters = true;
                state.errors.costCenters = null;
            })
            .addCase(fetchCCForWorkInProgress.fulfilled, (state, action) => {
                state.loading.costCenters = false;
                state.costCenters = action.payload?.Data || [];
            })
            .addCase(fetchCCForWorkInProgress.rejected, (state, action) => {
                state.loading.costCenters = false;
                state.errors.costCenters = action.payload;
                state.costCenters = [];
            });

        builder
            .addCase(fetchClientsByCCForWorkProgress.pending, (state) => {
                state.loading.clients = true;
                state.errors.clients = null;
            })
            .addCase(fetchClientsByCCForWorkProgress.fulfilled, (state, action) => {
                state.loading.clients = false;
                state.clients = action.payload?.Data || [];
            })
            .addCase(fetchClientsByCCForWorkProgress.rejected, (state, action) => {
                state.loading.clients = false;
                state.errors.clients = action.payload;
                state.clients = [];
            });

        builder
            .addCase(fetchSubClientsByClientForWP.pending, (state) => {
                state.loading.subClients = true;
                state.errors.subClients = null;
            })
            .addCase(fetchSubClientsByClientForWP.fulfilled, (state, action) => {
                state.loading.subClients = false;
                state.subClients = action.payload?.Data || [];
            })
            .addCase(fetchSubClientsByClientForWP.rejected, (state, action) => {
                state.loading.subClients = false;
                state.errors.subClients = action.payload;
                state.subClients = [];
            });

        builder
            .addCase(fetchClientPOforWP.pending, (state) => {
                state.loading.poNumbers = true;
                state.errors.poNumbers = null;
            })
            .addCase(fetchClientPOforWP.fulfilled, (state, action) => {
                state.loading.poNumbers = false;
                state.poNumbers = action.payload?.Data || [];
            })
            .addCase(fetchClientPOforWP.rejected, (state, action) => {
                state.loading.poNumbers = false;
                state.errors.poNumbers = action.payload;
                state.poNumbers = [];
            });

        builder
            .addCase(saveWorkProgress.pending, (state) => {
                state.loading.save = true;
                state.errors.save = null;
                state.saveStatus = 'pending';
            })
            .addCase(saveWorkProgress.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;

                const responseStr = typeof action.payload?.Data === 'string'
                    ? action.payload.Data
                    : (typeof action.payload === 'string' ? action.payload : '');

                if (responseStr === 'Submited') {
                    state.saveStatus = 'success';
                } else {
                    state.saveStatus = 'failed';
                    state.errors.save = responseStr || 'Save failed';
                }
            })
            .addCase(saveWorkProgress.rejected, (state, action) => {
                state.loading.save = false;
                state.errors.save = action.payload;
                state.saveStatus = 'failed';
                state.saveResult = null;
            });
    },
});

export const {
    clearClients,
    clearSubClients,
    clearPONumbers,
    clearSaveResult,
    resetWorkInProgress,
} = workInProgressSlice.actions;

// Selectors
// =========
export const selectCostCentersArray = (state) => {
    const cc = state.workInProgress.costCenters;
    return Array.isArray(cc) ? cc : [];
};
export const selectCostCentersLoading = (state) => state.workInProgress.loading.costCenters;

export const selectClientsArray = (state) => {
    const c = state.workInProgress.clients;
    return Array.isArray(c) ? c : [];
};
export const selectClientsLoading = (state) => state.workInProgress.loading.clients;

export const selectSubClientsArray = (state) => {
    const sc = state.workInProgress.subClients;
    return Array.isArray(sc) ? sc : [];
};
export const selectSubClientsLoading = (state) => state.workInProgress.loading.subClients;

export const selectPONumbersArray = (state) => {
    const po = state.workInProgress.poNumbers;
    return Array.isArray(po) ? po : [];
};
export const selectPONumbersLoading = (state) => state.workInProgress.loading.poNumbers;

export const selectSaveStatus = (state) => state.workInProgress.saveStatus;
export const selectSaveLoading = (state) => state.workInProgress.loading.save;
export const selectSaveError = (state) => state.workInProgress.errors.save;

export default workInProgressSlice.reducer;
